import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { locationLabel } from '../../utils/terminology';

/** Parse a template's tags (JSON string or array) and test for a tag. */
const templateHasTag = (template: { tags?: string[] | string }, tag: string): boolean => {
  if (Array.isArray(template.tags)) {
    return template.tags.includes(tag);
  }
  if (typeof template.tags === 'string') {
    try {
      const parsedTags = JSON.parse(template.tags);
      return Array.isArray(parsedTags) && parsedTags.includes(tag);
    } catch {
      return false;
    }
  }
  return false;
};

interface User {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface Team {
  team_id: string;
  team_name: string;
  description: string;
}

interface JobTag {
  job_tag_id: string;
  tag_name: string;
  description: string;
}

interface CheckTemplate {
  template_id: string;
  template_name: string;
  description: string;
  tags?: string[] | string;
}

interface Location {
  location_id: string;
  location_name: string;
}

const AssignChecks: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [checkName, setCheckName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | null>(new Date());
  const [selectedTemplate, setSelectedTemplate] = useState<CheckTemplate | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [frequency, setFrequency] = useState('once');
  
  // Data lists
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [templates, setTemplates] = useState<CheckTemplate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  
  // Selected items
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [selectedJobTags, setSelectedJobTags] = useState<JobTag[]>([]);
  
  // Business permissions state
  const [businessPermissions, setBusinessPermissions] = useState<{
    allowSafetyChecks: boolean;
    allowLoanChecks: boolean;
    allowLocationChecks: boolean;
    useSiteTerminology: boolean;
  }>({
    allowSafetyChecks: true, // Default to true, will be updated after fetching
    allowLoanChecks: true, // Default to true, will be updated after fetching
    allowLocationChecks: false,
    useSiteTerminology: false
  });

  // First fetch business permissions on mount
  useEffect(() => {
    fetchBusinessPermissions();
  }, []);

  // Then fetch all other data when business permissions are loaded
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [
          usersResponse,
          teamsResponse,
          jobTagsResponse,
          templatesResponse,
          locationsResponse
        ] = await Promise.all([
          axios.get(`${API_BASE_URL}/users`),
          axios.get(`${API_BASE_URL}/teams`),
          axios.get(`${API_BASE_URL}/job-tags`),
          axios.get(`${API_BASE_URL}/check-templates`),
          axios.get(`${API_BASE_URL}/locations`)
        ]);

        setUsers(usersResponse.data);
        setTeams(teamsResponse.data);
        setJobTags(jobTagsResponse.data);
        
        // Filter templates based on business permissions
        let filteredTemplates: CheckTemplate[] = templatesResponse.data;

        // Location-tagged templates are only available when location checks are enabled
        if (!businessPermissions.allowLocationChecks) {
          filteredTemplates = filteredTemplates.filter(
            (template: CheckTemplate) => !templateHasTag(template, 'location')
          );
        }

        if (!businessPermissions.allowSafetyChecks && !businessPermissions.allowLoanChecks) {
          // Neither safety nor loan checks allowed — only location templates (if enabled) remain
          filteredTemplates = filteredTemplates.filter(
            (template: CheckTemplate) => templateHasTag(template, 'location')
          );
        } else if (!businessPermissions.allowSafetyChecks) {
          // Only show loan (and location) templates when safety checks are disabled
          filteredTemplates = filteredTemplates.filter(
            (template: CheckTemplate) => templateHasTag(template, 'loan') || templateHasTag(template, 'location')
          );
        } else if (!businessPermissions.allowLoanChecks) {
          // Filter out loan templates when loan checks are disabled
          filteredTemplates = filteredTemplates.filter(
            (template: CheckTemplate) => !templateHasTag(template, 'loan')
          );
        }
        
        setTemplates(filteredTemplates);
        setLocations(locationsResponse.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load data');
        setLoading(false);
      }
    };

    // Only fetch data after business permissions are loaded
    if (businessPermissions) {
      fetchData();
    }
  }, [businessPermissions]);

  const fetchBusinessPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      
      if (!token || !businessId) {
        console.warn('No authentication token or business ID found');
        setBusinessPermissions({
          allowSafetyChecks: true, // Default to true when no business context
          allowLoanChecks: true, // Default to true when no business context
          allowLocationChecks: false,
          useSiteTerminology: false
        });
        return;
      }

      console.log('Fetching business permissions for business:', businessId);
      const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Business permissions response:', response.data);
      
      const businessData = response.data;
      setBusinessPermissions({
        // Handle database values: 1 = true, 0 = false, null/undefined = true (default)
        allowSafetyChecks: businessData.allow_safety_checks !== 0,
        allowLoanChecks: businessData.allow_loan_checks !== 0,
        allowLocationChecks: businessData.allow_location_checks === 1 || businessData.allow_location_checks === true,
        useSiteTerminology: businessData.use_site_terminology === 1 || businessData.use_site_terminology === true
      });
    } catch (error) {
      console.error('Error fetching business permissions:', error);
      // Default to true on error for backwards compatibility
      setBusinessPermissions({
        allowSafetyChecks: true,
        allowLoanChecks: true,
        allowLocationChecks: false,
        useSiteTerminology: false
      });
    }
  };

  const isLocationTemplate = selectedTemplate ? templateHasTag(selectedTemplate, 'location') : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await axios.post(`${API_BASE_URL}/checks`, {
        check_name: checkName,
        description,
        template_id: selectedTemplate?.template_id,
        location_id: selectedLocation?.location_id,
        due_date: dueDate,
        frequency,
        assigned_users: selectedUsers.map(u => u.user_id),
        assigned_teams: selectedTeams.map(t => t.team_id),
        assigned_job_tags: selectedJobTags.map(j => j.job_tag_id)
      });

      setSuccess('Check assigned successfully!');
      // Reset form
      setCheckName('');
      setDescription('');
      setDueDate(new Date());
      setSelectedTemplate(null);
      setSelectedLocation(null);
      setSelectedUsers([]);
      setSelectedTeams([]);
      setSelectedJobTags([]);
    } catch (err) {
      setError('Failed to assign check');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Assign New Check
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Check Name"
                  value={checkName}
                  onChange={(e) => setCheckName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={templates}
                  getOptionLabel={(option) => option.template_name}
                  value={selectedTemplate}
                  onChange={(_, newValue) => setSelectedTemplate(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Check Template" required />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={locations}
                  getOptionLabel={(option) => option.location_name}
                  value={selectedLocation}
                  onChange={(_, newValue) => setSelectedLocation(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        isLocationTemplate
                          ? `${locationLabel(businessPermissions.useSiteTerminology)} to Check`
                          : locationLabel(businessPermissions.useSiteTerminology)
                      }
                      required
                      helperText={
                        isLocationTemplate
                          ? `This template runs against a ${locationLabel(businessPermissions.useSiteTerminology, { lowercase: true })} — the selected ${locationLabel(businessPermissions.useSiteTerminology, { lowercase: true })} is the target of the check (no asset involved).`
                          : undefined
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Due Date"
                    value={dueDate}
                    onChange={(newValue: Date | null) => setDueDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={frequency}
                    label="Frequency"
                    onChange={(e) => setFrequency(e.target.value)}
                  >
                    <MenuItem value="once">Once</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{ mb: 2 }}
                  >
                    <Tab label="Users" />
                    <Tab label="Teams" />
                    <Tab label="Tags" />
                  </Tabs>

                  {activeTab === 0 && (
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                      value={selectedUsers}
                      onChange={(_, newValue) => setSelectedUsers(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign to Users"
                          placeholder="Select users"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={`${option.first_name} ${option.last_name}`}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  )}

                  {activeTab === 1 && (
                    <Autocomplete
                      multiple
                      options={teams}
                      getOptionLabel={(option) => option.team_name}
                      value={selectedTeams}
                      onChange={(_, newValue) => setSelectedTeams(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign to Teams"
                          placeholder="Select teams"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.team_name}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  )}

                  {activeTab === 2 && (
                    <Autocomplete
                      multiple
                      options={jobTags}
                      getOptionLabel={(option) => option.tag_name}
                      value={selectedJobTags}
                      onChange={(_, newValue) => setSelectedJobTags(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign to Tags"
                          placeholder="Select tags"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.tag_name}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Assign Check
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AssignChecks; 